<script lang="ts">
    import { createEventDispatcher, get_current_component, onMount } from 'svelte/internal'
    import Fa from 'svelte-fa'
    import { faAngleLeft, faEdit, faPlusCircle, faSave, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
    import Zact from '../../_common/z/zact.svelte'
    import Zgrid from '../../_common/z/zgrid.svelte'
    import Zframe from '../../_common/z/zframe.svelte'
    import { Category } from '../../../modules/financialApi/categories/category'
    import { CategoryTypeEnum, CategoryTypeFriendlyEnum } from '../../../modules/financialApi/categories/category.enum'
    import type { CreateCategoryDto } from '../../../modules/financialApi/categories/dto/create-category.dto'
    import { UpdateCategoryDto } from '../../../modules/financialApi/categories/dto/update-category.dto'
    import { GridBadge, GridBadgeClassEnum, GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../../_common/z/zgrid'
    import { EntryTypeEnum } from '../../../modules/financialApi/entries/entry.enum'
    import Content from '../../_common/content.svelte'
    const dispatch = createEventDispatcher()

    function sendClose() {
        dispatch('close')
    }

    export let categoryId = null
    export let form: boolean = false
    export let creditDebit: EntryTypeEnum = null

    const me = get_current_component()
    let show = false
    let step = 1
    let entities: Category[] = []
    let entity: Category = new Category()

    interface iCategoryType {
        id: CategoryTypeEnum
        label: string
    }
    let types: iCategoryType[] = []
    function loadCategoryTypes() {
        for (const id of Object.values(CategoryTypeEnum)) {
            if (creditDebit === EntryTypeEnum.credit && !CategoryTypeEnum[id].startsWith('credito_')) continue
            if (creditDebit === EntryTypeEnum.debit && !CategoryTypeEnum[id].startsWith('debito_')) continue
            const item: iCategoryType = {
                id: id,
                label: CategoryTypeFriendlyEnum[id],
            }
            types.push(item)
        }
        let utils = window.api.utils.utils
        types.sort(utils.dynamicSort(false, 'label'))
        types = types
    }

    async function addDefaults() {
        await window.api.financials.categories.addDefaults()
        await loadEntities()
    }

    async function editEntity(id: string) {
        try {
            show = true
            entity = new Category()
            entity = await window.api.financials.categories.getOne(id)
            console.log('category', entity)
            step = 2
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function newEntity() {
        entity = new Category()
        step = 2
    }

    async function createEntity() {
        try {
            show = true
            const dto: CreateCategoryDto = {
                label: entity.label,
                type: entity.type,
            }
            entity = await window.api.financials.categories.create(dto)
            if (form) {
                sendClose()
                return
            }
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function updateEntity() {
        try {
            show = true
            const dto: UpdateCategoryDto = {
                label: entity.label,
                type: entity.type,
            }
            await window.api.financials.categories.update(entity.categoryId, dto)
            if (form) {
                sendClose()
                return
            }
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function saveEntity() {
        if (entity.categoryId) {
            updateEntity()
        } else {
            createEntity()
        }
    }

    async function loadEntities() {
        try {
            show = true
            step = 1
            entities = await window.api.financials.categories.getAll()
            entities = entities.sort((a, b) => {
                const firstCharA = a.type.charAt(0).toLowerCase()
                const firstCharB = b.type.charAt(0).toLowerCase()
                return firstCharA.localeCompare(firstCharB)
            })
            loadGrid()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        // grid
        grid = new GridClass()
        // grid head
        grid.title = 'Categorias Financeiras'
        grid.subtitle = ''
        grid.fontSize = '15px'
        grid.cols.push(new GridCol({ content: 'Crédito / Débito', type: GridColTypeEnum.badge }))
        grid.cols.push(new GridCol({ content: 'Nome da categoria', type: GridColTypeEnum.string, maxLength: 100 }))
        grid.cols.push(new GridCol({ content: 'Classificação' }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        // grid lines
        for (const item of entities) {
            const btnEdit = new GridButton({
                callback: () => editEntity(item.categoryId),
                icon: faEdit,
                label: 'Editar',
            })
            let line: GridLine = new GridLine()
            line.slots.push({
                content: new GridBadge({
                    label: item.type.startsWith('credito_') ? 'crédito' : 'débito',
                    class: item.type.startsWith('credito_') ? GridBadgeClassEnum.badge_blue : GridBadgeClassEnum.badge_danger,
                }),
            })
            line.slots.push({ content: item.label })
            line.slots.push({ content: CategoryTypeFriendlyEnum[item.type] })
            line.slots.push({ content: [btnEdit] })
            grid.lines.push(line)
        }
    }

    async function removeEntity() {
        if (!confirm('Deseja mesmo excluir este registro?')) {
            return
        }
        try {
            show = true
            await window.api.financials.categories.remove(entity.categoryId)
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function evaluateError(error) {
        window.error(error)
    }

    function showEntities() {
        step = 1
    }

    onMount(async () => {
        loadCategoryTypes()
        await loadEntities()
        if (form) {
            if (categoryId) {
                await editEntity(categoryId)
            } else {
                newEntity()
            }
        }
    })
</script>

<Content footer={true}>
    {#if step === 1}
        <Zact>
            <div class="spacer" />
            <button class="btn btn-primary" on:click={newEntity}><Fa icon={faPlusCircle} /> Nova categoria</button>
        </Zact>
        <Zgrid class="mt-3" {grid} />
        {#if entities && entities.length < 20}
            <div class="alert alert-primary text-center mt-3">
                <div>Adicione as categorias padrão de forma automática.</div>
                <div>
                    <button on:click={addDefaults} class="btn btn-secondary">Adicionar</button>
                </div>
            </div>
        {/if}
    {/if}
    {#if step === 2}
        <Zact>
            {#if !form}
                <button class="btn btn-secondary" on:click={showEntities}><Fa icon={faAngleLeft} /> Voltar</button>
            {/if}
            <div class="spacer" />
            {#if !!entity?.categoryId}
                <button class="btn btn-warning me-1" on:click={removeEntity}><Fa icon={faTrashAlt} /> Excluir</button>
            {/if}
            <button class="btn btn-primary" on:click={saveEntity}><Fa icon={faSave} /> Salvar</button>
        </Zact>

        <Zframe title="Categoria Financeira" class="mt-3">
            <div class="row">
                <div class="col">
                    <div class="input-group">
                        <label for="" class="input-group-text">Nome da categoria</label>
                        <input type="text" bind:value={entity.label} class="form-control" />
                    </div>
                </div>
                <div class="col">
                    <div class="input-group">
                        <label for="" class="input-group-text">Classificação</label>
                        <select class="form-select" bind:value={entity.type} name="" id="">
                            <option value="" />
                            {#each types as type}
                                <option value={type.id}>{type.label}</option>
                            {/each}
                        </select>
                    </div>
                </div>
            </div>
        </Zframe>
    {/if}
</Content>

<style>
</style>
